import { ChevronRight, RotateCcw, TriangleAlert } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { useT } from '@/i18n/useT';
import { ApiError, apiErrorMessage } from '@/lib/api';
import { cn } from '@/lib/utils';

export type ErrorStateProps = {
  error: unknown;
  onRetry?: (() => void) | undefined;
  compact?: boolean | undefined;
  className?: string | undefined;
};

type ErrorDetails = { status?: number; code?: string; requestId?: string; message?: string };

function readRequestId(details: unknown): string | undefined {
  if (typeof details !== 'object' || details === null || !('requestId' in details)) {
    return undefined;
  }
  const value = (details as { requestId?: unknown }).requestId;
  return typeof value === 'string' ? value : undefined;
}

export function errorDetails(error: unknown): ErrorDetails | null {
  if (error instanceof ApiError) {
    const requestId = error.requestId ?? readRequestId(error.details);
    return {
      status: error.status,
      code: error.code,
      ...(requestId !== undefined ? { requestId } : {}),
    };
  }
  if (error instanceof Error) {
    return { code: error.name, message: error.message };
  }
  return null;
}

export function ErrorState({ error, onRetry, compact = false, className }: ErrorStateProps) {
  const t = useT();
  const message = apiErrorMessage(error);
  const details = errorDetails(error);

  return (
    <div
      role="alert"
      data-slot="error-state"
      className={cn(
        'flex flex-col items-center gap-3 text-center',
        compact ? 'py-6' : 'py-16',
        className,
      )}
    >
      <TriangleAlert className="size-8 text-destructive" aria-hidden />
      <p className={cn('font-medium', compact ? 'text-base' : 'text-lg')}>{t('errors.title')}</p>
      <p className="max-w-md text-sm text-muted-foreground">{message}</p>
      {onRetry && (
        <Button variant="outline" size="sm" onClick={onRetry}>
          <RotateCcw aria-hidden />
          {t('common.retry')}
        </Button>
      )}
      {details && (
        <Collapsible className="flex w-full max-w-md flex-col items-center gap-2">
          <CollapsibleTrigger asChild>
            <Button variant="ghost" size="sm" className="group text-muted-foreground">
              <ChevronRight
                aria-hidden
                className="transition-transform group-data-[state=open]:rotate-90"
              />
              {t('common.details')}
            </Button>
          </CollapsibleTrigger>
          <CollapsibleContent className="w-full">
            <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 rounded-md border bg-muted/40 p-3 text-left font-mono text-xs">
              {details.status !== undefined && (
                <>
                  <dt className="text-muted-foreground">{t('errors.status')}</dt>
                  <dd>{details.status}</dd>
                </>
              )}
              {details.code !== undefined && (
                <>
                  <dt className="text-muted-foreground">{t('errors.code')}</dt>
                  <dd className="break-all">{details.code}</dd>
                </>
              )}
              {details.requestId !== undefined && (
                <>
                  <dt className="text-muted-foreground">{t('errors.requestId')}</dt>
                  <dd className="break-all">{details.requestId}</dd>
                </>
              )}
              {details.message !== undefined && (
                <>
                  <dt className="text-muted-foreground">{t('common.details')}</dt>
                  <dd className="break-words">{details.message}</dd>
                </>
              )}
            </dl>
          </CollapsibleContent>
        </Collapsible>
      )}
    </div>
  );
}
